import { cacheAllModuleFiles, updateModuleCache } from '@/store/sync/cachingUtils';
import { SubmittedAction } from '@/actions/index';
import dataUtils from '@/common/dataUtils';
import { performInMemorySearch } from '@/components/search/searchStore';
import i18n from '@/plugins/i18n';
import { SYNC_STATES } from '@/store/sync/sync';
import moduleIndex from './index';
import moduleId from './moduleId';
import wagenbuchApi from './wagenbuchApi';
import MODULE_ID from './moduleId';

const ROOT = { root: true };

const state = {
  reports: [],
  reportsById: {},
  fzByTyp: {},
  fahrzeuge: [],
  ereignisse: [],
  klassifizierungen: [],
  klassifizierungenByFzgTypeIdFk: {},
  skizzenByFzName: [],
  wbConfigs: undefined,
  comments: [],
  selectedFz: undefined,
  selectedFahrzeugObj: null, // This is the additional object to use for further infos (because typeIdFk is missing so far)
  tab: 'mangel',
  formInput: {}, // this
  navigationItems: {},
  dataLoaded: false,
  selectedFzId: '',
};
const getters = {
  /** ------------------------------------------------------------------------------------------------------------------
   * Module Implementation for unread badge counts by instance.
   * IMPLEMENT this getter to enable unread counter badges
   * -------------------------------------------------------------------------------------------------------------------
   */
  getUnreadCountByModuleInstance: (state) => (instance) => {
    let unreadCount = 0;
    // TODO: implement
    return unreadCount;
  },
};
const mutations = {
  setData(state, serverData) {
    Object.keys(serverData).forEach((key) => {
      // state[key] = Object.freeze(serverData[key]);
      state[key] = serverData[key];
    });
    state.dataLoaded = true;
  },
  setSelectedFzId(state, value) {
    state.selectedFzId = value;
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for UPDATING Module counters
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  countModuleItems(state) {},
  changeTab: (state, value) => (state.tab = value),
  setSelectedFz: (state, value) => {
    state.selectedFz = value;
    state.selectedFahrzeugObj = state.fahrzeuge.find((f) => f.fzName === value);
    console.debug('Selected Fahrzeug', JSON.stringify(value, null, 2));
    console.debug('Selected Fahrzeug', JSON.stringify(state.selectedFahrzeugObj, null, 2));
  },
};

const actions = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for IN-MEMORY SEARCH
   * IMPLEMENT this getter to enable module in-memory search
   * Call the action in search/searchStore (search for "Module in-memory searches" there)
   * Search all documents containing all of the space separated search words in state.filterCriteria
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  searchModuleInMemoryContainingSearchTerm({ state }, { filterCriteria, options }) {
    return performInMemorySearch({
      moduleId,
      items: state.reports,
      titleSearchAttr: 'ereignisName',
      contentSearchAttr: null,
      searchCriteriaArray: filterCriteria,
      options,
    }).concat(
      performInMemorySearch({
        moduleId,
        items: state.reports,
        titleSearchAttr: 'beschrieb',
        contentSearchAttr: null,
        searchCriteriaArray: filterCriteria,
        options,
      })
    );
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Updates the browsers cache storage oevpad-api/modules/{moduleId} with the exact same data structure as received by the module sync api call!
   * Reason: This mutation MUST be called whenever data has changed, e.g. user_state data. It assures that the application cache is up-to-date
   *         when the user synchronized/updates in offline mode.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async updateModuleApplicationCache({ state }) {
    // don't update application cache
    // const { reports, fahrzeuge, fzByTyp, klassifizierungen, klassifizierungenByFzgTypeIdFk, skizzenByFzName, ereignisse, wbConfigs } = state;
    // await updateModuleCache(moduleIndex.api, {
    //   reports,
    //   fahrzeuge,
    //   fzByTyp,
    //   klassifizierungen,
    //   klassifizierungenByFzgTypeIdFk,
    //   skizzenByFzName,
    //   ereignisse,
    //   wbConfigs,
    // });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeFiles({ commit, dispatch, state }) {
    let reportFiles = state.reports
      .filter((f) => !!f.pdf)
      .map((f) => {
        return { filename: f.pdf, checksum: f.checksum, requestSuffix: '/report' };
      });
    let ereignisFiles = state.reports
      .filter((f) => !!f.file)
      .map((f) => {
        return { filename: f.file, checksum: f.checksum };
      });
    let skizzenFiles = state.skizzenByFzName.map((f) => {
      // return { filename: f.filename, checksum: f.checksum,requestSuffix:'/wagenbuch-fzg' };
      return { filename: f.filename, checksum: f.checksum };
    });
    let files = reportFiles.concat(ereignisFiles).concat(skizzenFiles);
    await cacheAllModuleFiles({ files, filenameAttribute: 'filename', moduleIndex, commit, dispatch });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeData({ commit, rootState, rootGetters }) {
    try {
      const response = await wagenbuchApi.getData();
      let {
        reports,
        fahrzeuge,
        fzByTyp,
        klassifizierungen,
        klassifizierungenByFzgTypeIdFk,
        skizzenByFzName,
        ereignisse,
        wbConfigs,
        comments,
        commentsUserState,
        cockpitHasCommentUserStates,
      } = response.data;

      let reportsById = dataUtils.buildIdMap(reports, 'id');
      let moduleConfig = rootGetters['configs/getInstanceModuleConfig']({ moduleType: moduleIndex.moduleType });
      let commentsUserStateByCommentId = dataUtils.buildIdMap(commentsUserState, 'commentIdFk');

      reports.forEach((r) => {
        r._displayLocation = '';
        r._moduleTitle = moduleConfig.title;
        r._path = `/wagenbuch?tab=${r.typ}&reportId=${r.id}`;
      });

      comments.forEach((c) => {
        let report = reportsById[c.reportIdFk];
        if (report) {
          if (report.comments) {
            report.comments.push(c);
          } else {
            report.comments = [c];
          }
        }

        if (cockpitHasCommentUserStates) {
          if (commentsUserStateByCommentId[c.id]) {
            c._userState = commentsUserStateByCommentId[c.id];
          } else {
            c._userState = {
              proto: true,
              commentIdFk: c.commentIdFk,
              dateread: null,
            };
          }
        } else {
          c._userState = {
            proto: true,
            commentIdFk: c.commentIdFk,
            dateread: new Date().toISOString().replace('T', ' ').substring(0, 19),
          };
        }
      });

      commit('setData', {
        reports,
        reportsById,
        fahrzeuge,
        fzByTyp,
        klassifizierungen,
        klassifizierungenByFzgTypeIdFk,
        skizzenByFzName,
        ereignisse,
        wbConfigs,
        comments,
      });
    } catch (err) {
      console.error(err);
      commit('sync/setSyncState', { moduleId, syncState: SYNC_STATES.FAILURE, msg: err }, ROOT);
      throw err;
    }
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Dispatched in store/sync !!!
   * Gets dispatched after all sync processes have been completed.
   * This is the place to implement any post sync processes.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async onSyncComplete({ commit, dispatch, rootState, rootGetters }) {
    // Implement this method if necessary
  },

  async sendSchadenformular({ commit, dispatch }, { instance, wbType, module, formData }) {
    // add to sendbox
    const action = new SubmittedAction({
      userInfo: i18n.t(`module.wagenbuch.type.${wbType}.submitted`),
      title: i18n.t(`module.wagenbuch.type.${wbType}.submitted`),
      instance: instance,
      module: module,
      //displayLocation: document._displayLocation,
      objectType: 'schadenformular',
      sendboxItem: {
        ...formData,
        userdatemodified: new Date(),
        _type: 'schadenformular',
        wbType,
      },
    });
    commit('sendbox/addAction', action, ROOT);
    //Use await here to avoid sending sendbox twice (after redirect to /wagenbuch it would be sent
    // again because the action gets added to the sendbox and two sendSendbox actions are running)
    await dispatch('sendbox/sendSendbox', undefined, ROOT);
    commit('snackbar/displaySnackbar', { action }, ROOT);

    setTimeout(async () => {
      dispatch('sync/invalidateModuleCacheAndSync', { moduleId: MODULE_ID }, { root: true });
    }, 65 * 1000);
  },

  async sendEreignis({ commit, dispatch }, { instance, module, formData, medias }) {
    // add to sendbox
    const action = new SubmittedAction({
      userInfo: i18n.t(`module.wagenbuch.type.ereignis.submitted`),
      title: i18n.t(`module.wagenbuch.type.ereignis.submitted`),
      instance: instance,
      module: module,
      //displayLocation: document._displayLocation,
      objectType: 'ereignis',
      sendboxItem: {
        ...formData,
        medias: medias,
        userdatemodified: new Date(),
        _type: 'ereignis',
      },
    });
    commit('sendbox/addAction', action, ROOT);
    //Use await here to avoid sending sendbox twice (after redirect to /wagenbuch it would be sent
    // again because the action gets added to the sendbox and two sendSendbox actions are running)
    await dispatch('sendbox/sendSendbox', undefined, ROOT);
    commit('snackbar/displaySnackbar', { action }, ROOT);

    setTimeout(async () => {
      dispatch('sync/invalidateModuleCacheAndSync', { moduleId: MODULE_ID }, { root: true });
    }, 65 * 1000);
  },

  async sendComment({ commit, dispatch }, { instance, module, formData }) {
    // add to sendbox
    const action = new SubmittedAction({
      userInfo: i18n.t('action.submitted.msg', { object: i18n.t('module.wagenbuch.comment') }),
      title: 'Kommentar hinzugefügt',
      instance: instance,
      module: module,
      objectType: 'pdfComment',
      sendboxItem: {
        ...formData,
        userdatemodified: new Date(),
        _type: 'pdfComment',
      },
    });
    commit('sendbox/addAction', action, ROOT);
    await dispatch('sendbox/sendSendbox', undefined, ROOT);
    commit('snackbar/displaySnackbar', { action }, ROOT);
  },

  async sendCommentUserState({ commit, dispatch }, { instance, module, formData }) {
    const action = new SubmittedAction({
      userInfo: i18n.t('action.submitted.msg', { object: i18n.t('module.wagenbuch.comment') }),
      title: 'Kommentar gelesen',
      instance: instance,
      module: module,
      objectType: 'pdfCommentUserState',
      sendboxItem: {
        ...formData,
        userdatemodified: new Date(),
        _type: 'pdfCommentUserState',
      },
    });
    commit('sendbox/addAction', action, ROOT);
    await dispatch('sendbox/sendSendbox', undefined, ROOT);
    //commit('snackbar/displaySnackbar', { action }, ROOT);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
